import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  StateReport,
  StateReportWithJobs,
  StateReportFilters,
  StateReportQueueStats,
  StateReportOptions
} from '../models/state-report.model';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface PaginatedApiResponse {
  success: boolean;
  data?: StateReport[];
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
  error?: string;
}

interface PaginatedReportsResponse {
  reports: StateReport[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Batch generation response from API
 */
interface BatchGenerationResponse {
  created: number;
  reports: StateReport[];
  errors: { organizationId: string; error: string }[];
}

/**
 * State Report Service (Admin)
 *
 * Service for managing state compliance reports in the admin portal.
 * Communicates with /admin/state-reports endpoints.
 */
@Injectable({
  providedIn: 'root'
})
export class StateReportService {
  private readonly apiUrl = `${environment.apiUrl}/admin/state-reports`;

  constructor(private http: HttpClient) {}

  /**
   * List state reports with optional filters
   */
  async listReports(filters?: StateReportFilters): Promise<PaginatedReportsResponse> {
    const params = new URLSearchParams();

    if (filters?.organizationId) {
      params.append('organizationId', filters.organizationId);
    }
    if (filters?.status) {
      params.append('status', filters.status);
    }
    if (filters?.state) {
      params.append('state', filters.state);
    }
    if (filters?.startDate) {
      params.append('startDate', filters.startDate);
    }
    if (filters?.endDate) {
      params.append('endDate', filters.endDate);
    }
    if (filters?.search) {
      params.append('search', filters.search);
    }
    if (filters?.page) {
      params.append('page', filters.page.toString());
    }
    if (filters?.pageSize) {
      params.append('limit', filters.pageSize.toString());
    }

    const queryString = params.toString();
    const url = queryString ? `${this.apiUrl}?${queryString}` : this.apiUrl;

    const response = await firstValueFrom(
      this.http.get<PaginatedApiResponse>(url)
    );

    if (response.success && response.data) {
      // Backend returns data as array directly with total/page/limit at root level
      return {
        reports: response.data,
        total: response.total ?? 0,
        page: response.page ?? 1,
        pageSize: response.limit ?? 20
      };
    }

    return { reports: [], total: 0, page: 1, pageSize: 20 };
  }

  /**
   * Get a single report with job information
   */
  async getReport(id: string): Promise<StateReportWithJobs | null> {
    // Backend returns { success, data: { report, jobs } }
    interface ReportApiResponse {
      success: boolean;
      data?: {
        report: StateReport;
        jobs: StateReportWithJobs['jobs'];
      };
      error?: string;
    }

    const response = await firstValueFrom(
      this.http.get<ReportApiResponse>(`${this.apiUrl}/${id}`)
    );

    if (response.success && response.data) {
      // Merge report fields with jobs array
      return {
        ...response.data.report,
        jobs: response.data.jobs || []
      };
    }

    return null;
  }

  /**
   * Delete a report (soft delete)
   */
  async deleteReport(id: string): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`)
    );

    return response.success;
  }

  /**
   * Regenerate a report (create new generation job)
   */
  async regenerateReport(id: string): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<void>>(`${this.apiUrl}/${id}/regenerate`, {})
    );

    return response.success;
  }

  /**
   * Retry a failed report generation job
   */
  async retryReport(id: string): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<void>>(`${this.apiUrl}/${id}/retry`, {})
    );

    return response.success;
  }

  /**
   * Get queue statistics
   */
  async getQueueStats(): Promise<StateReportQueueStats> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<StateReportQueueStats>>(`${this.apiUrl}/queue-stats`)
    );

    if (response.success && response.data) {
      return response.data;
    }

    return { pending: 0, active: 0, completed: 0, failed: 0 };
  }

  /**
   * Generate batch of state reports for multiple organizations
   * Each organization will have reports generated for all states with field data
   */
  async generateBatch(
    organizationIds: string[],
    startDate: string,
    endDate: string,
    reportType: string,
    options: StateReportOptions
  ): Promise<BatchGenerationResponse> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<BatchGenerationResponse>>(`${this.apiUrl}/batch`, {
        organizationIds,
        startDate,
        endDate,
        reportType,
        options
      })
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || 'Failed to generate batch reports');
  }

  /**
   * Get unique states for an organization based on its field locations
   * Used for auto-detecting which states an organization operates in
   */
  async getOrganizationStates(orgId: string): Promise<string[]> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<{ states: string[] }>>(
        `${environment.apiUrl}/admin/organizations/${orgId}/report-states`
      )
    );

    if (response.success && response.data) {
      return response.data.states;
    }

    return [];
  }

  /**
   * Update report dates (admin only)
   */
  async updateReportDates(id: string, startDate: string, endDate: string): Promise<StateReport | null> {
    const response = await firstValueFrom(
      this.http.put<ApiResponse<StateReport>>(`${this.apiUrl}/${id}`, { startDate, endDate })
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || 'Failed to update report dates');
  }
}
